import { Button } from "../ui/button";
import { TestimonialCardPreview } from "./TestimonialCardPreview";
import { VersionDiffViewer } from "./VersionDiffViewer";
import { AlertCircle, Save, Trash2, X, Check } from "lucide-react";
import { type TestimonialItem, safeParseLinks } from "./useTestimonialsManager";
import { LinksConfigSection } from "./ProjectsSubSections";
import { TestimonialFormFields } from "./TestimonialFormFields";

const diffFields = [
  { key: "author_en", label: "Author (English)" },
  { key: "author_tr", label: "Author (Turkish)" },
  { key: "role_en", label: "Role (English)" },
  { key: "role_tr", label: "Role (Turkish)" },
  { key: "company_en", label: "Company (English)" },
  { key: "company_tr", label: "Company (Turkish)" },
  { key: "quote_en", label: "Quote (English)", isLongText: true },
  { key: "quote_tr", label: "Quote (Turkish)", isLongText: true },
  { key: "links", label: "Reference Links", isLongText: true },
];

interface StagingPanelProps {
  activeDraft: any;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
}

function StagingPanel({ activeDraft, onSaveDraft, onDiscardDraft }: StagingPanelProps) {
  return (
    <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
        <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
        <span>
          {activeDraft
            ? "⚠️ Staged Staging Draft has unpublished modifications."
            : "✅ Working copy is synced to database."}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {activeDraft && onDiscardDraft && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDiscardDraft}
            className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
          </Button>
        )}
        {onSaveDraft && (
          <Button
            type="button"
            size="sm"
            onClick={onSaveDraft}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
          </Button>
        )}
      </div>
    </div>
  );
}

interface TestimonialEditFormProps {
  editingTestimonial: Partial<TestimonialItem>;
  setEditingTestimonial: React.Dispatch<React.SetStateAction<Partial<TestimonialItem> | null>>;
  activeDraft: any;
  liveItem: TestimonialItem | undefined;
  onSave: () => void;
  onSaveDraft: () => void;
  onDiscardDraft: () => void;
  onCancel: () => void;
}

export function TestimonialEditForm({
  editingTestimonial,
  setEditingTestimonial,
  activeDraft,
  liveItem,
  onSave,
  onSaveDraft,
  onDiscardDraft,
  onCancel,
}: TestimonialEditFormProps) {
  const updateField = (key: keyof TestimonialItem, value: any) => {
    setEditingTestimonial((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const isNew = !liveItem;

  return (
    <div className="p-5 border border-border/60 rounded-2xl bg-card/65 space-y-4 max-w-3xl text-left">
      <h5 className="font-bold text-base text-brand-primary">
        {isNew ? "Add Testimonial" : "Edit Testimonial"}
      </h5>

      {!isNew && (
        <StagingPanel
          activeDraft={activeDraft}
          onSaveDraft={onSaveDraft}
          onDiscardDraft={onDiscardDraft}
        />
      )}

      <TestimonialFormFields editingTestimonial={editingTestimonial} onChange={updateField} />

      <LinksConfigSection
        links={safeParseLinks(editingTestimonial.links)}
        onChange={(newLinks) => updateField("links", newLinks)}
      />

      <div className="border-t border-border/40 pt-6">
        <TestimonialCardPreview item={editingTestimonial} />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button variant="outline" onClick={onCancel} className="font-semibold">
          <X className="h-4 w-4 mr-2" /> Cancel
        </Button>
        <Button onClick={onSave} className="bg-brand-primary text-white font-semibold">
          <Check className="h-4 w-4 mr-2" /> Publish Live
        </Button>
      </div>

      {!isNew && liveItem && (
        <div className="pt-4 border-t border-border/40">
          <VersionDiffViewer
            live={{
              ...liveItem,
              links: typeof liveItem.links === "string" ? liveItem.links : JSON.stringify(liveItem.links || [])
            }}
            draft={{
              ...editingTestimonial,
              links: typeof editingTestimonial.links === "string" ? editingTestimonial.links : JSON.stringify(editingTestimonial.links || [])
            }}
            fieldsToCompare={diffFields}
          />
        </div>
      )}
    </div>
  );
}

export default TestimonialEditForm;
